"use client"

import { useEffect, useRef } from "react"
import { mountGrainGradient, type GrainMount, type GrainParams } from "@/components/tracker/digest-next/grain-shader"
import styles from "./public-watch-cta.module.css"

const PARAMS: GrainParams = {
  colors: ["#DDDDDD", "#929292", "#5C5C5C", "#212121", "#141417"],
  colorBack: "#00000000",
  softness: 0.5,
  intensity: 0.51,
  noise: 0,
  shape: "wave",
  scale: 1,
  rotation: 0,
  offsetX: 0,
  offsetY: 0,
}

export function PublicWatchCtaGrain() {
  const rootRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = rootRef.current
    const host = hostRef.current
    if (!root || !host || !("IntersectionObserver" in window)) return
    const desktop = window.matchMedia("(min-width: 1280px)")
    const tablet = window.matchMedia("(min-width: 640px)")
    let near = false
    let pageHidden = document.hidden
    let renderer: GrainMount | null = null
    let generation = 0
    const release = () => {
      generation += 1
      host.dataset.ready = "false"
      renderer?.release()
      renderer = null
    }
    const start = () => {
      release()
      if (!near || pageHidden) return
      const current = generation
      const [width, height] = desktop.matches ? [620, 460] : tablet.matches ? [620, 488] : [358, 322]
      renderer = mountGrainGradient(host, PARAMS, {
        frame: 27320.790999900364,
        speed: 0,
        minPixelRatio: 2,
        maxPixelCount: width * height * 4,
        onReady: () => { if (current === generation) host.dataset.ready = "true" },
        onFail: () => { host.dataset.ready = "false" },
        onLost: () => { host.dataset.ready = "false" },
      })
    }
    const observer = new IntersectionObserver(([entry]) => {
      near = entry.isIntersecting
      start()
    }, { rootMargin: "200px" })
    const visibility = () => { pageHidden = document.hidden; start() }
    const pagehide = () => { pageHidden = true; release() }
    const pageshow = () => { pageHidden = document.hidden; start() }
    observer.observe(root)
    desktop.addEventListener("change", start)
    tablet.addEventListener("change", start)
    document.addEventListener("visibilitychange", visibility)
    window.addEventListener("pagehide", pagehide)
    window.addEventListener("pageshow", pageshow)
    return () => {
      observer.disconnect()
      desktop.removeEventListener("change", start)
      tablet.removeEventListener("change", start)
      document.removeEventListener("visibilitychange", visibility)
      window.removeEventListener("pagehide", pagehide)
      window.removeEventListener("pageshow", pageshow)
      release()
    }
  }, [])

  return (
    <div ref={rootRef} className={styles.grain} aria-hidden="true">
      <picture className={styles.fallback}>
        <source media="(min-width: 1280px)" srcSet="/images/public-watch-cta/grain-desktop@2x.webp" />
        <source media="(min-width: 640px)" srcSet="/images/public-watch-cta/grain-tablet@2x.webp" />
        <img src="/images/public-watch-cta/grain-mobile@2x.webp" width="358" height="322" alt="" loading="lazy" decoding="async" />
      </picture>
      <div ref={hostRef} className={styles.shader} />
    </div>
  )
}
